(() => {
  'use strict';

  const maisonSectionSelectors = {
    hero: '[data-maison-product-root]',
    features: '#maison-product-features',
    realHomes: '#maison-product-real-homes',
    reviews: '#maison-product-reviews',
    faq: '#maison-product-faq',
  };


  // De kleur waar net op geklikt is: haal er de naam en het rondje uit, zodat de
  // melding kan zeggen welke kleur er geladen wordt in plaats van 'je selectie'.
  function maisonFinishDetails(node) {
    const item = node.closest('.swatch-view-item') || node;
    const bronnen = [item.getAttribute('data-tav-kleur'), item.getAttribute('data-value'),
                     item.getAttribute('aria-label'),
                     item.getAttribute('title'), node.getAttribute('title'),
                     item.querySelector('.swatch-label, .swatch-name')?.textContent,
                     item.textContent];
    const naam = (bronnen.find((waarde) => waarde && waarde.trim()) || '').trim().replace(/\s+/g, ' ');
    let beeld = '';
    const afbeelding = item.querySelector('img');
    if (afbeelding) beeld = afbeelding.currentSrc || afbeelding.getAttribute('src') || '';
    if (!beeld) {
      for (const kandidaat of [item, ...item.querySelectorAll('*')]) {
        const laag = window.getComputedStyle(kandidaat).backgroundImage;
        const gevonden = /url\(["']?(.+?)["']?\)/.exec(laag || '');
        if (gevonden) { beeld = gevonden[1]; break; }
      }
    }
    let kleur = '';
    if (!beeld) {
      for (const kandidaat of [item.querySelector('.swatch-image') || item, item]) {
        const laag = window.getComputedStyle(kandidaat).backgroundColor;
        if (laag && !/rgba?\(0, 0, 0, 0\)|transparent/.test(laag)) { kleur = laag; break; }
      }
    }
    return { naam, beeld, kleur };
  }

  function maisonDestination(raw, current) {
    try {
      const url = new URL(raw, current);
      const from = new URL(current);
      const productPath = /^(.*\/products\/)[^/]+\/?$/;
      const path = url.pathname.match(productPath);
      const previous = from.pathname.match(productPath);
      if (url.origin !== from.origin || !path || !previous || path[1] !== previous[1]) return null;
      url.searchParams.delete('sections');
      url.searchParams.delete('section_id');
      url.searchParams.set('view', 'maison');
      if (!url.searchParams.has('preview_theme_id') && from.searchParams.has('preview_theme_id')) {
        url.searchParams.set('preview_theme_id', from.searchParams.get('preview_theme_id'));
      }
      return url;
    } catch (_) { return null; }
  }

  function maisonReadContext(scope) {
    const node = scope.querySelector('[data-maison-product-context]');
    const context = JSON.parse(node?.textContent || 'null');
    const product = context?.product;
    if (context?.version !== 1 || context.enabled !== true || !context.sectionId
      || !product?.id || !product.handle || !product.url || !Array.isArray(product.variants)
      || !product.variants.some((variant) => String(variant.id) === String(product.variantId))) {
      throw new Error('maison:invalid-product-context');
    }
    return context;
  }

  function maisonSectionMap(document) {
    return Object.fromEntries(Object.entries(maisonSectionSelectors).map(([role, selector]) => {
      const element = document.querySelector(selector)?.closest('[id^="shopify-section-"]');
      if (!element) throw new Error(`maison:missing-section:${role}`);
      return [role, element.id.slice('shopify-section-'.length)];
    }));
  }

  function maisonParseHead(context, destination, Parser) {
    if (typeof context.head !== 'string' || typeof context.structuredHead !== 'string') throw new Error('maison:missing-metadata');
    const head = new Parser().parseFromString(context.head, 'text/html');
    const canonical = head.querySelector('link[rel="canonical"]');
    if (!head.querySelector('title')?.textContent.trim() || !canonical
      || new URL(canonical.getAttribute('href'), destination).pathname.replace(/\/$/, '') !== destination.pathname.replace(/\/$/, '')) {
      throw new Error('maison:invalid-metadata');
    }
    const structured = new Parser().parseFromString(context.structuredHead, 'text/html');
    const scripts = [...structured.querySelectorAll('script[type="application/ld+json"][data-maison-product-head]')];
    if (!scripts.length) throw new Error('maison:missing-breadcrumbs');
    scripts.forEach((node) => {
      const data = JSON.parse(node.textContent);
      if (!['BreadcrumbList', 'FAQPage'].includes(data['@type'])) throw new Error('maison:invalid-metadata');
    });
    return { head, scripts };
  }

  function maisonParseSections(data, ids, destination, current, Parser) {
    const sections = {};
    if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('maison:invalid-sections');
    for (const [role, id] of Object.entries(ids)) {
      if (typeof data[id] !== 'string' || !data[id].trim()) throw new Error(`maison:empty-section:${role}`);
      const doc = new Parser().parseFromString(data[id], 'text/html');
      const wrapper = doc.getElementById(`shopify-section-${id}`);
      if (!wrapper || !wrapper.querySelector(maisonSectionSelectors[role])) throw new Error(`maison:invalid-section:${role}`);
      // JSON and JSON-LD are data. All executable code lives in the explicit lifecycle.
      wrapper.querySelectorAll('script').forEach((script) => {
        if (!['application/json', 'application/ld+json'].includes(script.type)) script.remove();
      });
      sections[role] = wrapper;
    }
    const context = maisonReadContext(sections.hero);
    const target = maisonDestination(context.product.url, destination.href);
    const root = sections.hero.querySelector(maisonSectionSelectors.hero);
    const input = root.querySelector('[name="id"]');
    if (context.sectionId !== ids.hero || target?.pathname.replace(/\/$/, '') !== destination.pathname.replace(/\/$/, '')
      || String(root.dataset.productId) !== String(context.product.id)
      || String(input?.value) !== String(context.product.variantId)
      || context.locale !== current.locale || context.country !== current.country || context.currency !== current.currency) {
      throw new Error('maison:destination-mismatch');
    }
    const requestedVariant = destination.searchParams.get('variant');
    if (context.product.variants.some((variant) => String(variant.id) === requestedVariant)
      && String(context.product.variantId) !== requestedVariant) throw new Error('maison:variant-mismatch');
    return { sections, context, metadata: maisonParseHead(context, destination, Parser) };
  }

  function maisonDeadline(work, milliseconds, signal) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        signal?.removeEventListener('abort', aborted);
        callback(value);
      };
      const aborted = () => finish(reject, signal.reason || new DOMException('Aborted', 'AbortError'));
      const timer = setTimeout(() => finish(reject, new Error('maison:timeout')), milliseconds);
      signal?.addEventListener('abort', aborted, { once: true });
      if (signal?.aborted) aborted();
      Promise.resolve(work).then((value) => finish(resolve, value), (error) => finish(reject, error));
    });
  }

  // Fetches are replaceable; commits own global app state and must finish serially.
  class MaisonNavigationQueue {
    constructor(handlers) {
      this.handlers = handlers;
      this.sequence = 0;
      this.phase = 'idle';
      this.pending = null;
      this.running = false;
      this.stopped = false;
    }
    request(url, options = {}) {
      if (this.stopped) return;
      this.pending = { id: ++this.sequence, url, options };
      this.latest = this.pending;
      if (this.phase === 'fetching') this.fetchController?.abort();
      this.handlers.busy(true, this.pending);
      return this.run();
    }
    async run() {
      if (this.running) return;
      this.running = true;
      try {
        while (this.pending && !this.stopped) {
          const request = this.pending;
          this.pending = null;
          try {
            this.phase = 'fetching';
            this.fetchController = new AbortController();
            const result = await this.handlers.fetch(request, this.fetchController.signal);
            if (request.id !== this.sequence || this.stopped) continue;
            this.phase = 'committing';
            const committed = await this.handlers.commit(result, request);
            if (request.id !== this.sequence || this.stopped) continue;
            this.phase = 'ready';
            this.handlers.ready(committed, request);
          } catch (error) {
            if (this.stopped) break;
            if (request.id !== this.sequence && this.phase === 'fetching') continue;
            this.stopped = true;
            this.handlers.fail(error, this.pending || request);
          }
        }
      } finally {
        this.running = false;
        if (!this.stopped && !this.pending) {
          this.phase = 'idle';
          this.handlers.busy(false);
        }
      }
    }
    dispose() {
      this.stopped = true;
      this.pending = null;
      this.fetchController?.abort();
    }
  }

  // Node's VM test harness exercises the same queue/parser used by the storefront.
  if (typeof module === 'object' && module.exports) {
    module.exports = { MaisonNavigationQueue, maisonDestination, maisonReadContext, maisonSectionMap, maisonParseSections, maisonParseHead, maisonDeadline };
  }
  if (typeof window === 'undefined' || !window.document) return;

  let maisonController;

  class MaisonProductNavigation {
    constructor(root) {
      this.root = root;
      this.context = maisonReadContext(root);
      this.ids = maisonSectionMap(document);
      this.apps = window.MaisonProductApps;
      this.abort = new AbortController();
      this.pendingEntry = false;
      this.locked = false;
      this.cartPending = false;
      this.cartWaiters = new Set();
      this.busyButtons = new Map();
      this.transition = window.MaisonProductTransition ? new window.MaisonProductTransition() : null;
      this.status = document.createElement('p');
      this.status.className = 'maison-product-navigation-status';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.dataset.maisonImplementation = 'product-navigation-announcement';
      document.body.append(this.status);
      this.queue = new MaisonNavigationQueue({
        busy: (value, request) => this.setBusy(value, request),
        fetch: (request, signal) => this.fetch(request, signal),
        commit: (result, request) => this.commit(result, request),
        ready: (result, request) => this.ready(result, request),
        fail: (error, request) => this.fallback(error, request),
      });
      this.lockObserver = new MutationObserver(() => this.enforcePurchaseLock());
      const options = { signal: this.abort.signal, capture: true };
      document.addEventListener('click', (event) => this.click(event), options);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') this.click(event);
      }, options);
      document.addEventListener('submit', (event) => {
        if (this.locked && this.root.contains(event.target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, options);
      document.addEventListener('theme:cart:add', (event) => {
        if (this.locked && this.root.contains(event.detail?.button)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        } else if (this.root.contains(event.detail?.button)) {
          this.cartPending = true;
          this.cartButton = event.detail.button;
        }
      }, options);
      ['theme:product:add', 'theme:product:add-error', 'theme:cart:error'].forEach((name) => {
        document.addEventListener(name, (event) => {
          if (!this.cartPending || (event.detail?.button && event.detail.button !== this.cartButton)) return;
          this.cartPending = false;
          this.cartWaiters.forEach((resolve) => resolve());
          this.cartWaiters.clear();
        }, { signal: this.abort.signal });
      });
      window.addEventListener('popstate', (event) => this.pop(event), { signal: this.abort.signal });
      window.addEventListener('hashchange', () => { if (!this.locked) this.saveEntry(); }, { signal: this.abort.signal });
      window.addEventListener('scroll', () => {
        if (!this.locked && !this.scrollFrame) this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          if (!this.locked) this.saveEntry();
        });
      }, { passive: true, signal: this.abort.signal });
      this.previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      this.saveEntry();
    }

    saveEntry() {
      this.syncVariant();
      let selection;
      try { selection = this.apps?.capture(this.root, this.context); } catch (_) { /* Incomplete app state is not a restorable choice. */ }
      history.replaceState({ ...history.state, maisonProduct: {
        url: location.href, productId: String(this.context.product.id), variantId: String(this.context.product.variantId),
        scroll: { x: scrollX, y: scrollY },
        selection: selection ? { bundle: selection.bundle, quantity: selection.quantity } : null,
      } }, '', location.href);
    }

    syncVariant() {
      const id = this.root.querySelector('[name="id"]')?.value;
      if (this.context.product.variants.some((variant) => String(variant.id) === String(id))) this.context.product.variantId = id;
    }

    navigate(raw, options = {}) {
      const url = maisonDestination(raw, location.href);
      if (!url || !this.root.isConnected || this.queue.stopped) return false;
      const active = new URL(this.context.product.url, location.href);
      if (url.pathname === active.pathname && !this.locked && options.history !== 'none') return false;
      if (!this.locked) {
        this.syncVariant();
        let snapshot;
        try {
          snapshot = this.apps?.capture(this.root, this.context);
          if (!this.apps || !snapshot) throw new Error('maison:missing-app-adapter');
          this.apps.preflight(snapshot);
        } catch (_) { return false; }
        if (options.history !== 'none') this.saveEntry();
        this.selection = snapshot;
        this.viewport = { x: scrollX, y: scrollY };
      }
      this.queue.request(url, { history: 'push', source: 'swatch', ...options });
      return true;
    }

    click(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const purchase = target.closest('[data-add-to-cart], [data-maison-sticky-purchase] button');
      if (this.locked && purchase && this.root.contains(purchase)) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (event.button != null && event.button !== 0)) return;
      const swatch = target.closest('[swatch-url]');
      const anchor = swatch?.closest('a');
      if (!swatch || !this.root.contains(swatch) || anchor?.hasAttribute('download')
        || anchor?.target && anchor.target !== '_self') return;
      if (this.navigate(swatch.getAttribute('swatch-url'), { source: 'swatch' })) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    pop(event) {
      const url = maisonDestination(location.href, new URL(this.context.product.url, location.origin).href);
      const activeVariant = this.root.querySelector('[name="id"]')?.value;
      if (url?.pathname === new URL(this.context.product.url, location.origin).pathname
        && (!url.searchParams.get('variant') || url.searchParams.get('variant') === activeVariant) && !this.locked) {
        const viewport = event.state?.maisonProduct?.scroll;
        if (viewport) window.scrollTo({ left: viewport.x, top: viewport.y, behavior: 'instant' });
        else if (url.hash) document.getElementById(decodeURIComponent(url.hash.slice(1)))?.scrollIntoView();
        return;
      }
      if (!url || !this.navigate(url.href, { history: 'none', source: 'history', restore: event.state?.maisonProduct })) location.reload();
    }

    setBusy(value, request) {
      this.locked = value;
      if (value) {
        this.root.setAttribute('aria-busy', 'true');
        this.lockObserver.disconnect();
        this.lockObserver.observe(this.root, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
        this.root.querySelectorAll('[data-maison-color-pending]').forEach((node) => node.removeAttribute('data-maison-color-pending'));
        this.root.querySelectorAll('[swatch-url]').forEach((node) => {
          const url = maisonDestination(node.getAttribute('swatch-url'), location.href);
          if (url?.pathname === request.url.pathname) node.setAttribute('data-maison-color-pending', '');
        });
        this.enforcePurchaseLock();
        const gekozen = this.root.querySelector('[data-maison-color-pending]');
        const finish = gekozen ? maisonFinishDetails(gekozen) : null;
        const sjabloon = this.context.labels.loadingNamed;
        const tekst = finish?.naam && sjabloon
          ? sjabloon.replace('[kleur]', finish.naam)
          : this.context.labels.loading;
        this.status.textContent = tekst;
        this.transition?.begin(this.root, tekst, finish?.beeld || '', finish?.kleur || '');
      } else {
        this.lockObserver.disconnect();
        this.root.removeAttribute('aria-busy');
        this.root.inert = false;
        this.root.querySelectorAll('[data-maison-color-pending]').forEach((node) => node.removeAttribute('data-maison-color-pending'));
        const variant = this.context.product.variants.find((item) => String(item.id) === String(this.context.product.variantId));
        this.busyButtons.forEach((wasDisabled, button) => {
          if (button.isConnected) button.disabled = wasDisabled || !variant?.available;
        });
        this.busyButtons.clear();
      }
    }

    enforcePurchaseLock() {
      if (!this.locked) return;
      this.root.querySelectorAll('[data-add-to-cart], [data-maison-sticky-purchase] button, .shopify-payment-button button').forEach((button) => {
        if (!this.busyButtons.has(button)) this.busyButtons.set(button, button.disabled);
        if (!button.disabled) button.disabled = true;
      });
    }

    async fetch(request, signal) {
      if (this.cartPending) {
        let done;
        try {
          await maisonDeadline(new Promise((resolve) => { done = resolve; this.cartWaiters.add(done); }), 10000, signal);
        } finally { this.cartWaiters.delete(done); }
      }
      const controller = new AbortController();
      const abort = () => controller.abort(signal.reason);
      signal.addEventListener('abort', abort, { once: true });
      try {
        const url = new URL(request.url);
        url.hash = '';
        url.searchParams.set('sections', Object.values(this.ids).join(','));
        return await maisonDeadline((async () => {
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) throw new Error(`maison:http:${response.status}`);
          const data = await response.json();
          const result = maisonParseSections(data, this.ids, request.url, this.context, DOMParser);
          if (this.selection?.required.bundles) {
            if (!window.MaisonKaching?.enabled) throw new Error('maison:missing-kaching-lifecycle');
            result.kachingData = await window.MaisonKaching.prepare(request.url, result.context, controller.signal);
          }
          return result;
        })(), 10000, signal);
      } finally {
        controller.abort();
        signal.removeEventListener('abort', abort);
      }
    }

    async commit(result, request) {
      const { context } = result;
      const snapshot = { ...this.selection, ...request.options.restore?.selection };
      const destination = new URL(request.url);
      destination.searchParams.set('variant', context.product.variantId);
      const state = { ...history.state, maisonProduct: { url: destination.href, productId: String(context.product.id), variantId: String(context.product.variantId) } };
      if (request.options.history === 'none' || this.pendingEntry) history.replaceState(state, '', destination);
      else history.pushState(state, '', destination);
      this.pendingEntry = request.options.history !== 'none';
      this.apps.beforeCommit(context, snapshot);
      // Adopt into the active document only after URL and app metadata agree.
      const sections = Object.fromEntries(Object.entries(result.sections).map(([role, section]) => [role, document.importNode(section, true)]));
      this.lockObserver.disconnect();
      const nextRoot = sections.hero.querySelector(maisonSectionSelectors.hero);
      nextRoot.inert = true;
      nextRoot.setAttribute('aria-busy', 'true');
      // Keep one real product root: the transition retains only decorative
      // geometry while the incoming apps initialize at their actual width.
      this.transition?.stage(this.root, nextRoot);
      for (const [role, id] of Object.entries(this.ids)) {
        const old = document.getElementById(`shopify-section-${id}`);
        if (!old) throw new Error(`maison:lost-section:${role}`);
        document.dispatchEvent(new CustomEvent('maison:content-unloading', { detail: { scope: old } }));
      }
      for (const [role, id] of Object.entries(this.ids)) document.getElementById(`shopify-section-${id}`).replaceWith(sections[role]);
      this.root = nextRoot;
      this.context = context;
      this.busyButtons.clear();
      this.setBusy(true, request);
      this.updateHead(result.metadata);
      // Variant hydration caches the selected media for galleries initialized later.
      Object.values(sections).forEach((scope) => document.dispatchEvent(new CustomEvent('maison:content-loaded', { detail: { scope } })));
      this.enforcePurchaseLock();
      this.refreshRecommendations(context);
      const controller = new AbortController();
      this.appController = controller;
      this.queue.phase = 'initializing-apps';
      try {
        const apps = await maisonDeadline(this.apps.mount(nextRoot, context, snapshot, { signal: controller.signal, kachingData: result.kachingData }), 10000, this.abort.signal);
        const variant = nextRoot.querySelector('[name="id"]');
        if (String(variant?.value) !== String(context.product.variantId)) throw new Error('maison:purchase-mismatch');
        if (request.id === this.queue.sequence && !this.queue.stopped) {
          await this.transition?.settled(nextRoot, controller.signal);
          if (request.id === this.queue.sequence && !this.queue.stopped) {
            const viewport = request.options.restore?.scroll || this.viewport;
            if (viewport) window.scrollTo({ left: viewport.x, top: viewport.y, behavior: 'instant' });
            await this.transition?.reveal(nextRoot);
          }
        }
        return { context, apps, destination };
      } finally { controller.abort(); this.appController = null; }
    }

    updateHead({ head: doc, scripts }) {
      const selectors = ['title', 'link[rel="canonical"]', 'meta[name="description"]', 'meta[name^="twitter:"]', 'meta[property^="og:"]'];
      selectors.forEach((selector) => {
        document.head.querySelectorAll(selector).forEach((node) => node.remove());
        doc.querySelectorAll(selector).forEach((node) => document.head.append(node.cloneNode(true)));
      });
      document.head.querySelectorAll('[data-maison-product-head]').forEach((node) => node.remove());
      scripts.forEach((node) => {
        document.head.append(node.cloneNode(true));
      });
    }

    refreshRecommendations(context) {
      const old = document.querySelector('maison-product-recommendations');
      if (!old) return;
      const url = new URL(old.dataset.url, location.href);
      url.searchParams.set('product_id', context.product.id);
      this.recommendationController?.abort();
      const controller = new AbortController();
      this.recommendationController = controller;
      document.dispatchEvent(new CustomEvent('maison:content-unloading', { detail: { scope: old } }));
      old.replaceChildren();
      old.hidden = true;
      old.dataset.url = url.pathname + url.search;
      old.dataset.loaded = 'true';
      maisonDeadline((async () => {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('maison:recommendations');
        return response.text();
      })(), 10000, controller.signal)
        .then((html) => {
          if (controller.signal.aborted || !old.isConnected || this.context !== context) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('[data-maison-recommendations-section]');
          if (!content) return;
          content.querySelectorAll('script:not([type="application/json"]):not([type="application/ld+json"])').forEach((node) => node.remove());
          old.append(content);
          old.hidden = false;
          document.dispatchEvent(new CustomEvent('maison:content-loaded', { detail: { scope: old } }));
        }).catch(() => { /* Optional recommendations stay empty; never retain the old product's rail. */ })
        .finally(() => controller.abort());
    }

    ready(result, request) {
      this.pendingEntry = false;
      // Incoming form state was initialized while locked; derive availability afresh.
      const available = result.context.product.variants.find((item) => String(item.id) === String(result.context.product.variantId))?.available;
      this.busyButtons.forEach((_, button) => this.busyButtons.set(button, !available));
      this.setBusy(false);
      const viewport = request.options.restore?.scroll || this.viewport;
      if (viewport) window.scrollTo({ left: viewport.x, top: viewport.y, behavior: 'instant' });
      const selected = this.root.querySelector('variant-swatch-king [aria-checked="true"][swatch-url], variant-swatch-king [aria-checked="true"]');
      if (request.options.source !== 'history') selected?.focus({ preventScroll: true });
      this.status.textContent = result.apps?.selectionReset ? result.context.labels.reset : result.context.labels.ready;
      this.saveEntry();
      const detail = { navigationId: request.id, source: request.options.source, url: result.destination.href,
        productId: String(result.context.product.id), variantId: String(result.context.product.variantId) };
      document.dispatchEvent(new CustomEvent('maison:product-changed', { detail }));
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()) {
        try { window.Shopify.analytics?.publish?.('maison:product_switched', detail); } catch (_) { /* Analytics cannot block commerce. */ }
      }
    }

    fallback(error, request) {
      this.transition?.dispose();
      console.warn('[Maison product navigation]', error.message);
      const target = window.MaisonKaching?.nativeURL(request.url.href) || request.url.href;
      if (this.pendingEntry || request.options.history === 'none') location.replace(target);
      else location.assign(target);
    }

    dispose() {
      this.queue.dispose();
      this.transition?.dispose();
      this.abort.abort();
      this.appController?.abort();
      this.recommendationController?.abort();
      this.lockObserver.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      this.status.remove();
      history.scrollRestoration = this.previousScrollRestoration;
    }
  }

  function maisonInitializeNavigation() {
    if (window.MaisonKachingNativeFallback) return;
    const root = document.querySelector('[data-maison-product-root][data-maison-dynamic-colors="true"]');
    if (maisonController?.root === root && !maisonController.queue.stopped) return;
    maisonController?.dispose();
    maisonController = null;
    if (!root) return;
    try { maisonController = new MaisonProductNavigation(root); }
    catch (error) { console.warn('[Maison product navigation unavailable]', error.message); }
  }
  if (typeof module === 'object' && module.exports) module.exports.MaisonProductNavigation = MaisonProductNavigation;
  window.MaisonProductNavigation = {
    navigate: (url, options) => maisonController?.navigate(url, options) || false,
    fallbackToNative(error) {
      if (!maisonController) return false;
      const request = maisonController.queue.running && maisonController.queue.latest
        || { url: new URL(location.href), options: { history: 'none' } };
      maisonController.queue.dispose();
      maisonController.appController?.abort();
      maisonController.fallback(error, request);
      return true;
    },
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', maisonInitializeNavigation, { once: true });
  else maisonInitializeNavigation();
  document.addEventListener('shopify:section:load', maisonInitializeNavigation);
  window.addEventListener('pagehide', () => {
    if (maisonController) maisonController.resumeRequiresReload = maisonController.locked;
    maisonController?.dispose();
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    // An interrupted app commit has uncertain global commerce state. A clean
    // document is safer than resuming partially initialized third-party widgets.
    if (maisonController?.resumeRequiresReload) location.reload();
    else maisonInitializeNavigation();
  });
})();
